import { CHECKOUT_UI } from '../constants/orders'
import {
  RAZORPAY_CHECKOUT_SCRIPT,
  RAZORPAY_SCRIPT_ID,
  type RazorpayCheckoutPayload,
  type RazorpayCheckoutResult,
  type RazorpayConstructor,
} from '../constants/payments'

export async function loadRazorpayScript(): Promise<RazorpayConstructor> {
  const existing = razorpayConstructor()
  if (existing) return existing

  await new Promise<void>((resolve, reject) => {
    const script = document.createElement('script')
    script.id = RAZORPAY_SCRIPT_ID
    script.src = RAZORPAY_CHECKOUT_SCRIPT
    script.async = true
    script.onload = () => resolve()
    script.onerror = () => reject(new Error(CHECKOUT_UI.paymentScriptError))
    document.body.appendChild(script)
  })

  const ctor = razorpayConstructor()
  if (!ctor) throw new Error(CHECKOUT_UI.paymentScriptError)
  return ctor
}

export async function openRazorpayCheckout(
  payload: RazorpayCheckoutPayload,
  timeoutMs?: number,
): Promise<RazorpayCheckoutResult> {
  if (timeoutMs !== undefined && timeoutMs <= 0) return { status: 'expired' }

  const Razorpay = await loadRazorpayScript()
  return new Promise((resolve) => {
    let settled = false
    let timer = 0
    const finish = (result: RazorpayCheckoutResult) => {
      if (settled) return
      settled = true
      window.clearTimeout(timer)
      resolve(result)
    }
    const checkout = new Razorpay({
      key: payload.keyId,
      amount: payload.amount,
      currency: payload.currency,
      name: payload.name,
      description: payload.description,
      order_id: payload.orderId,
      prefill: payload.prefill,
      handler: (payment) => finish({ status: 'paid', payment }),
      modal: {
        ondismiss: () => finish({ status: 'dismissed' }),
      },
    })
    if (timeoutMs !== undefined) {
      timer = window.setTimeout(() => {
        try {
          checkout.close()
        } catch {
          // The payment window may already be closed.
        }
        finish({ status: 'expired' })
      }, timeoutMs)
    }
    checkout.open()
  })
}

function razorpayConstructor(): RazorpayConstructor | null {
  const ctor = (window as Window & { Razorpay?: RazorpayConstructor }).Razorpay
  return ctor ?? null
}
